<template>
    <div>
        <v-container v-if="isLoading">
            <LoadingComponent
                :isLoading="isLoading"
                loadingType="svg"
                :waitForVideoCompletion="true"
            />
        </v-container>
        <v-container v-else>
            <v-row class="d-flex justify-center ga-10">
                <v-col class="d-flex justify-center pt-12" cols="12">
                    <h1>SIGN IN</h1>
                </v-col>
                <v-col cols="12">
                    <v-form v-model="valid" @keydown.enter="signInWithEmail">
                        <v-text-field
                            variant="outlined"
                            v-model="email"
                            label="Email"
                            :rules="[rules.required, rules.email]"
                            required
                        ></v-text-field>
                        <v-text-field
                            variant="outlined"
                            v-model="password"
                            :append-inner-icon="
                                showPassword ? 'mdi-eye' : 'mdi-eye-off'
                            "
                            :rules="[rules.required]"
                            :type="showPassword ? 'text' : 'password'"
                            label="Password"
                            @click:append-inner="showPassword = !showPassword"
                            required
                        ></v-text-field>

                        <!-- Forgot Password Button -->
                        <v-btn
                            size="x-small"
                            variant="text"
                            class="text-blue"
                            @click="goToForgotPassword"
                        >
                            FORGOT YOUR PASSWORD?
                        </v-btn>
                    </v-form>
                </v-col>
                <v-col class="d-flex flex-column ga-5">
                    <v-btn
                        size="x-large"
                        block
                        color="primary"
                        @click="signInWithEmail"
                        :disabled="!valid"
                    >
                        SIGN IN
                    </v-btn>
                    <v-btn
                        size="x-large"
                        block
                        color="primary"
                        @click="signInWithGoogle"
                        >SIGN IN WITH GOOGLE
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import { handleSignIn } from "@/services/firebaseService";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { googleProvider, auth } from "@/firebaseConfig";
import LoadingComponent from "@/components/LoadingComponent.vue";

export default {
    name: "SignIn",
    components: {
        LoadingComponent,
    },
    data() {
        return {
            email: "",
            password: "",
            valid: false,
            showPassword: false,
            isLoading: false,
            rules: {
                required: (value) => !!value || "Required.",
                email: (value) =>
                    /.+@.+\..+/.test(value) || "E-mail must be valid.",
            },
        };
    },
    methods: {
        /**
         * Signs in a user with an email and password using Firebase Authentication.
         *
         * This method handles email/password authentication, manages the loading state,
         * and calls a common sign-in handler to complete the sign-in process.
         *
         * @async
         * @returns {Promise<void>} Resolves when the sign-in process is complete.
         *
         * @throws {Error} If the email/password sign-in process fails.
         *
         * @example
         * // Example usage in a Vue component
         * await this.signInWithEmail();
         */ async signInWithEmail() {
            this.isLoading = true;
            try {
                const userCredential = await signInWithEmailAndPassword(
                    auth,
                    this.email,
                    this.password
                );
                const user = userCredential.user;

                // Call the common sign-in handler
                // Pass `this.isLoading = false` as a callback to handleSignIn
                await handleSignIn(user, this.$router, (isLoading) => {
                    this.isLoading = isLoading;
                });
            } catch (error) {
                alert(error.message); // Notify the user of the error
                this.$router.push("/"); // Redirect to sign-in page
                this.isLoading = false; // Stop the spinner
            }
        },

        /**
         * Signs in a user with Google using Firebase Authentication.
         *
         * This method handles Google OAuth authentication, manages the loading state,
         * and calls a common sign-in handler to complete the sign-in process.
         *
         * @async
         * @returns {Promise<void>} Resolves when the Google sign-in process is complete.
         *
         * @throws {Error} If the Google sign-in process fails.
         *
         * @example
         * // Example usage in a Vue component
         * await this.signInWithGoogle();
         */
        async signInWithGoogle() {
            try {
                const userCredential = await signInWithPopup(
                    auth,
                    googleProvider
                );
                const user = userCredential.user;

                // Call the common sign-in handler
                await handleSignIn(user, this.$router);
            } catch (error) {
                alert(error.message); // Notify the user of the error
                this.$router.push("/"); // Redirect to sign-in page
                this.isLoading = false; // Stop the spinner
            }
        },

        /**
         * Redirects the user to the "Forgot Password" page.
         *
         * This method uses Vue Router to navigate to the `/forgotpassword` route.
         *
         * @example
         * // Redirect the user to the Forgot Password page
         * this.goToForgotPassword();
         */
        goToForgotPassword() {
            this.$router.push("/forgotpassword");
        },
    },
    beforeUnmount() {
        this.isLoading = false;
    },
};
</script>
